import { useStyles } from "./Videos.style";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import VideoCard from "../../components/common/VideoCard/VideoCard";
import { useQuery } from "@apollo/client";
import { VideoItem, VideoType } from "@onmo/graphql/graphql";
import { contentVideosGQL, newsGQL } from "../../graphql";
import LaunchPad from "../../components/common/ServiceSelection/LaunchPad";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import { Icon } from "@onmo/ui";
import clsx from "classnames";
import { map } from "lodash-es";
import { NewsCard } from "../../components/common/NewsCard";

const tabList = [
  { title: "Videos", icon: "icon-play-circle" },
  { title: "News", icon: "icon-bold-global" },
];

// iOS keeps firing resize/scroll while the layout reflows after a rotation
const VIEWPORT_SETTLE_DELAY = 700;

const isVideoFullscreen = () => {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitCurrentFullScreenElement?: Element | null;
  };
  if (
    doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.webkitCurrentFullScreenElement
  ) {
    return true;
  }
  // iOS Safari has no Fullscreen API on iPhone: an embedded video opens in the
  // native player instead, and the embed iframe holds focus while it is open.
  return document.activeElement?.tagName === "IFRAME";
};

const Videos: React.FC = () => {
  const classes = useStyles();
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(tabList[0]);
  const { i18n } = useTranslation();
  const { data: videosData } = useQuery(contentVideosGQL, {
    variables: {
      type: VideoType.Youtube,
      params: {
        maxResults: 50,
      },
    },
  });
  const { data: newsData } = useQuery(newsGQL, {
    variables: {
      params: {
        languages: i18n.language,
      },
    },
  });
  const videoCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const selectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeVideoIndexRef = useRef<number | null>(null);

  const videos = videosData?.contentVideos || [];

  const updateActiveVideoByCenter = useCallback(() => {
    if (isVideoFullscreen()) return;
    if (activeTab.title !== tabList[0].title || videos.length === 0) {
      setActiveVideoIndex(null);
      return;
    }

    const viewportCenter = window.innerHeight / 2;
    let nextActiveIndex: number | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    videoCardRefs.current.forEach((element, index) => {
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!isVisible) return;

      const itemCenter = (rect.top + rect.bottom) / 2;
      const distanceToCenter = Math.abs(itemCenter - viewportCenter);

      if (distanceToCenter < closestDistance) {
        closestDistance = distanceToCenter;
        nextActiveIndex = index;
      }
    });

    setActiveVideoIndex((prev) =>
      prev === nextActiveIndex ? prev : nextActiveIndex,
    );
  }, [activeTab.title, videos.length]);

  const handleVideoSelect = useCallback((index: number) => {
    if (selectTimerRef.current) clearTimeout(selectTimerRef.current);
    const el = videoCardRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    selectTimerRef.current = setTimeout(() => {
      setActiveVideoIndex(index);
    }, 400);
  }, []);

  useEffect(() => {
    videoCardRefs.current = videoCardRefs.current.slice(0, videos.length);
  }, [videos.length]);

  useEffect(() => {
    activeVideoIndexRef.current = activeVideoIndex;
  }, [activeVideoIndex]);

  const keepActiveVideoVisible = useCallback(() => {
    const index = activeVideoIndexRef.current;
    if (index === null) {
      updateActiveVideoByCenter();
      return;
    }
    if (isVideoFullscreen()) return;
    videoCardRefs.current[index]?.scrollIntoView({ block: "center" });
  }, [updateActiveVideoByCenter]);

  useEffect(() => {
    let scrollStopTimer: ReturnType<typeof setTimeout> | null = null;
    let viewportSettleTimer: ReturnType<typeof setTimeout> | null = null;
    let isViewportChanging = false;

    const onScroll = () => {
      if (isViewportChanging || isVideoFullscreen()) return;
      if (scrollStopTimer) clearTimeout(scrollStopTimer);
      scrollStopTimer = setTimeout(updateActiveVideoByCenter, 500);
    };

    const onViewportChange = () => {
      isViewportChanging = true;
      if (scrollStopTimer) clearTimeout(scrollStopTimer);
      if (viewportSettleTimer) clearTimeout(viewportSettleTimer);
      viewportSettleTimer = setTimeout(() => {
        isViewportChanging = false;
        keepActiveVideoVisible();
      }, VIEWPORT_SETTLE_DELAY);
    };

    const onFullscreenChange = () => {
      if (!isVideoFullscreen()) keepActiveVideoVisible();
    };

    updateActiveVideoByCenter();
    globalThis.addEventListener("resize", onViewportChange);
    globalThis.addEventListener("orientationchange", onViewportChange);
    document.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);

    return () => {
      if (scrollStopTimer) clearTimeout(scrollStopTimer);
      if (viewportSettleTimer) clearTimeout(viewportSettleTimer);
      globalThis.removeEventListener("resize", onViewportChange);
      globalThis.removeEventListener("orientationchange", onViewportChange);
      document.removeEventListener("scroll", onScroll);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullscreenChange,
      );
    };
  }, [keepActiveVideoVisible, updateActiveVideoByCenter]);

  return (
    <div className={classes.root}>
      <LaunchPad />
      <div className={classes.tabs}>
        {tabList.map((tab) => {
          const active = activeTab.title === tab.title;
          return (
            <div
              className={clsx(classes.tabItem, { active })}
              key={tab.title}
              onClick={() => setActiveTab(tab)}
            >
              <Icon
                name={tab.icon}
                className={clsx(classes.icon, { active })}
              />
              <Typography
                variant={
                  active
                    ? TypographyVariant.BodyB5Bold
                    : TypographyVariant.BodyB5Regular
                }
                color={
                  active ? TypographyColor.Default : TypographyColor.Silver
                }
              >
                {tab.title}
              </Typography>
            </div>
          );
        })}
      </div>
      <div className={classes.videoList}>
        {activeTab?.title === tabList[0]?.title &&
          (videosData?.contentVideos || [])?.map(
            (video: VideoItem, index: number) => (
              <VideoCard
                video={video}
                isPlayingVideo={index === activeVideoIndex}
                onContainerRef={(element) => {
                  videoCardRefs.current[index] = element;
                }}
                onClick={() => handleVideoSelect(index)}
                key={video?.id}
              />
            ),
          )}
        {activeTab?.title === tabList[1]?.title &&
          map(newsData?.news?.items, (news, idx) => (
            <NewsCard news={news} key={idx} />
          ))}
      </div>
    </div>
  );
};

export default Videos;
